import DesignTokens from '@tableau/design-tokens';
import { ButtonType, TabStyles } from '@tableau/widgets';
import React from 'react';

import { assertUnreachable } from '~commonInfra/utils/TypescriptHelpers';
import { Messages } from '~src/codegen/Localize';
import { ButtonWithSpinner } from '~src/commonInfra/components/ButtonWithSpinner';
import { EditConnectionReactActionUtils } from '~src/connectivity/components/EditConnection/utils/EditConnectionActionUtils';
import { ICheckConnectionResult } from '~src/datasource/dataConnection/serverApi/CheckConnectionRequest';
import { ATTENTION_COLOR } from '~src/settings/styles/CommonStyles';
import { cssClass } from '~src/style';
import { applyColor }from '~src/style/ColorService';

import { EditConnectionDialogState } from '../EditConnectionAction';

export type EditConnectionDialogTestConnectionProps = Readonly<{
  dialogState: EditConnectionDialogState;
  onTestConnection: (dialogState: EditConnectionDialogState) => Promise<ICheckConnectionResult>;
  // Whether this row's class has an OAuth-embedded/WIF chooser at all -- see
  // EditConnectionReactActionUtils.usesAuthChooser.
  usesAuthChooser: boolean;
}>;


type TestConnectionAction =
  { type: 'TEST_CONNECTION_START' } |
  { type: 'TEST_CONNECTION_FINISH', result: ICheckConnectionResult };

type TestConnectionState =
  | { name: 'idle' | 'testing' }
  | {
    name: 'testComplete';
    succeeded: boolean;
    connectionTestMessage: string;
  }
  | { name: 'unexpectedError' };


function reducer(state: TestConnectionState, action: TestConnectionAction): TestConnectionState {
  switch (action.type) {
    case 'TEST_CONNECTION_START':
      return { name: 'testing' };
    case 'TEST_CONNECTION_FINISH':
      if (!action.result) {
        return { name: 'unexpectedError'};
      }
      return {
        name: 'testComplete',
        succeeded: (action.result && action.result.succeeded),
        connectionTestMessage: (action.result && action.result.messages && action.result.messages.length > 0 && action.result.messages[0])
      };
    default:
      return state;
  }
}

export function EditConnectionDialogTestConnection(props: EditConnectionDialogTestConnectionProps) {

  const [state, dispatch] = React.useReducer(
    reducer,
    { name: 'idle' }
  );

  const onTestConnection = async () => {
    dispatch({ type: 'TEST_CONNECTION_START' });
    const testConnectionResult = await props.onTestConnection(props.dialogState);
    dispatch({ type: 'TEST_CONNECTION_FINISH', result: testConnectionResult });
  };


  const renderButton = () => {
    // W-22482525 / W-23640221 / W-24143571: Test Connection is disabled for OAuth, none, and
    // remote-query-agent rows, none of which get a meaningful server-side probe (OAuth's probe does not
    // reflect the saved/embedded credential; none has no credential to test; remote-query-agent routes
    // through the bridge agent), and for a WIF row until its WIF state is valid. Every other auth type
    // (password, keypair, ...) keeps the button active. Saving the edit is unaffected either way.
    const disabled = EditConnectionReactActionUtils.isTestConnectionDisabledForAuth(props.dialogState, props.usesAuthChooser);
    return (
      <div style={{ paddingTop: '24px' }}>
        <ButtonWithSpinner
          buttonType={ButtonType.Outline}
          onClick={onTestConnection}
          testId="edit-conn-button-signin"
          data-tb-test-id='edit-conn-button-signin'
          showSpinner={state.name === 'testing'}
          children={Messages.testConnection()}
          disabled={disabled}
        />
      </div>
    );
  };

  const renderConnectionTestOutcome = () => {
    switch (state.name) {
      case 'testComplete':
        const connectionMessage = state.succeeded ? Messages.testConnectionSuccess() : Messages.testConnectionFailure();
        const testOutcomeClassname: string = cssClass('edit-connection', 'test-outcome', {
          color: state.succeeded ? applyColor(DesignTokens.color.font.default.default, TabStyles.Typography.Color.Primary) : applyColor(DesignTokens.color.component.content.alert.default.error.default, ATTENTION_COLOR)
        });
        return (
          <div className={testOutcomeClassname} style={{ paddingTop: '6px' }} data-tb-test-id='edit-conn-text'>
            {connectionMessage}
            {state.connectionTestMessage && <div>{state.connectionTestMessage}</div>}
          </div>
        );
      case 'idle':
      case 'testing':
      case 'unexpectedError':
        return null;
      default:
        assertUnreachable(state);
        return null;
    }
  };

  return (
    <div style={{ textAlign: 'right', paddingBottom: '16px' }} data-tb-test-id='edit-conn-button-signin-and-text'>
      {renderButton()}
      {renderConnectionTestOutcome()}
    </div>
  );

}
